import type { FormField } from './form';

export type FormRatingIcon = 'star' | 'heart' | 'thumb' | 'smiley';

export type FormImageSource = 'Upload' | 'Url';
export type FormImageAlignment = 'Left' | 'Center' | 'Right';

export interface FormInlineImageSettings {
  imageSource?: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  alignment?: FormImageAlignment;
}

export type FormFieldMaskKind =
  | 'None'
  | 'Cpf'
  | 'Cnpj'
  | 'Phone'
  | 'Cep'
  | 'Currency'
  | 'Custom';

export type FormFieldContactBinding = 'None' | 'Name' | 'Email' | 'Phone';

export interface FormFieldMaskSettings {
  kind?: FormFieldMaskKind;
  pattern?: string;
}

export interface FormFieldSettings {
  ratingScale?: number;
  ratingIcon?: FormRatingIcon;
  imageSource?: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  alignment?: FormImageAlignment;
  youtubeUrl?: string;
  inlineImage?: FormInlineImageSettings;
  mask?: FormFieldMaskSettings;
  contactBinding?: FormFieldContactBinding;
}

export const FORM_FIELD_MASK_OPTIONS: {
  value: FormFieldMaskKind;
  label: string;
}[] = [
  { value: 'None', label: 'Nenhuma' },
  { value: 'Cpf', label: 'CPF' },
  { value: 'Cnpj', label: 'CNPJ' },
  { value: 'Phone', label: 'Telefone' },
  { value: 'Cep', label: 'CEP' },
  { value: 'Currency', label: 'Moeda' },
  { value: 'Custom', label: 'Personalizada' },
];

export const FORM_FIELD_CONTACT_BINDING_OPTIONS: {
  value: FormFieldContactBinding;
  label: string;
}[] = [
  { value: 'None', label: 'Nenhum' },
  { value: 'Name', label: 'Nome do contato' },
  { value: 'Email', label: 'E-mail do contato' },
  { value: 'Phone', label: 'Telefone do contato' },
];

export const RATING_SCALE_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const RATING_ICON_OPTIONS: {
  value: FormRatingIcon;
  label: string;
}[] = [
  { value: 'star', label: 'Estrela' },
  { value: 'heart', label: 'Coração' },
  { value: 'thumb', label: 'Polegar' },
  { value: 'smiley', label: 'Carinha' },
];

export const DEFAULT_RATING_SCALE = 5;
export const DEFAULT_RATING_ICON: FormRatingIcon = 'star';
export const DEFAULT_IMAGE_ALIGNMENT: FormImageAlignment = 'Center';

export function parseFormFieldSettings(settingsJson?: string): FormFieldSettings {
  if (!settingsJson)
  {
    return {};
  }

  try
  {
    const parsed = JSON.parse(settingsJson) as FormFieldSettings;
    return parsed && typeof parsed === 'object' ? parsed : {};
  }
  catch
  {
    return {};
  }
}

export function serializeFormFieldSettings(settings: FormFieldSettings): string {
  return JSON.stringify(settings);
}

export function clampRatingScale(value: number): number {
  if (value < 2)
  {
    return 2;
  }

  if (value > 10)
  {
    return 10;
  }

  return Math.round(value);
}

export function resolveRatingSettings(field: FormField): {
  scale: number;
  icon: FormRatingIcon;
} {
  const settings = parseFormFieldSettings(field.settingsJson);
  const icon = RATING_ICON_OPTIONS.some((item) => item.value === settings.ratingIcon)
    ? (settings.ratingIcon as FormRatingIcon)
    : DEFAULT_RATING_ICON;

  return {
    scale: clampRatingScale(settings.ratingScale ?? DEFAULT_RATING_SCALE),
    icon,
  };
}

export function buildRatingSettingsJson(
  scale: number,
  icon: FormRatingIcon,
  existingSettingsJson?: string
): string {
  const current = parseFormFieldSettings(existingSettingsJson);
  return serializeFormFieldSettings({
    ...current,
    ratingScale: clampRatingScale(scale),
    ratingIcon: icon,
  });
}

export function normalizeImageAlignment(
  value?: string
): FormImageAlignment {
  if (value === 'Left' || value === 'Right' || value === 'Center')
  {
    return value;
  }

  return DEFAULT_IMAGE_ALIGNMENT;
}

export function resolveImageBlockSettings(field: FormField): {
  imageSource: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  alignment: FormImageAlignment;
  mediaUrl?: string;
} {
  const settings = parseFormFieldSettings(field.settingsJson);
  const imageSource: FormImageSource =
    settings.imageSource === 'Url' ? 'Url' : 'Upload';

  return {
    imageSource,
    objectKey: settings.objectKey,
    externalUrl: settings.externalUrl,
    alignment: normalizeImageAlignment(settings.alignment),
    mediaUrl: field.mediaUrl,
  };
}

export function buildImageBlockSettingsJson(input: {
  imageSource: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  alignment?: FormImageAlignment;
}): string {
  return serializeFormFieldSettings({
    imageSource: input.imageSource,
    objectKey: input.objectKey,
    externalUrl: input.externalUrl,
    alignment: normalizeImageAlignment(input.alignment),
  });
}

export function resolveVideoBlockSettings(field: FormField): {
  youtubeUrl?: string;
} {
  const settings = parseFormFieldSettings(field.settingsJson);
  return {
    youtubeUrl: settings.youtubeUrl ?? field.mediaUrl,
  };
}

export function buildVideoBlockSettingsJson(youtubeUrl: string): string {
  return serializeFormFieldSettings({
    youtubeUrl: youtubeUrl.trim(),
  });
}

export function resolveInlineImageSettings(field: FormField): {
  imageSource: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
  alignment: FormImageAlignment;
  inlineImageUrl?: string;
} | null {
  const settings = parseFormFieldSettings(field.settingsJson);
  if (!settings.inlineImage)
  {
    return null;
  }

  const imageSource: FormImageSource =
    settings.inlineImage.imageSource === 'Url' ? 'Url' : 'Upload';

  return {
    imageSource,
    objectKey: settings.inlineImage.objectKey,
    externalUrl: settings.inlineImage.externalUrl,
    alignment: normalizeImageAlignment(settings.inlineImage.alignment),
    inlineImageUrl: field.inlineImageUrl,
  };
}

export function patchInlineImageSettings(
  settingsJson: string | undefined,
  inlineImage: FormInlineImageSettings | null
): string {
  const current = parseFormFieldSettings(settingsJson);
  if (!inlineImage)
  {
    const { inlineImage: _removed, ...rest } = current;
    return serializeFormFieldSettings(rest);
  }

  return serializeFormFieldSettings({
    ...current,
    inlineImage: {
      imageSource: inlineImage.imageSource,
      objectKey: inlineImage.objectKey,
      externalUrl: inlineImage.externalUrl,
      alignment: normalizeImageAlignment(inlineImage.alignment),
    },
  });
}

export function extractYoutubeVideoId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed)
  {
    return null;
  }

  try
  {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be')
    {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id && /^[\w-]{11}$/.test(id) ? id : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com')
    {
      const fromQuery = parsed.searchParams.get('v');
      if (fromQuery && /^[\w-]{11}$/.test(fromQuery))
      {
        return fromQuery;
      }

      const parts = parsed.pathname.split('/').filter(Boolean);
      if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live')
      {
        const id = parts[1];
        return id && /^[\w-]{11}$/.test(id) ? id : null;
      }
    }
  }
  catch
  {
    return null;
  }

  return null;
}

export function buildYoutubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}

export function resolveDisplayImageUrl(input: {
  mediaUrl?: string;
  inlineImageUrl?: string;
  imageSource?: FormImageSource;
  objectKey?: string;
  externalUrl?: string;
}): string | undefined {
  if (input.mediaUrl)
  {
    return input.mediaUrl;
  }

  if (input.inlineImageUrl)
  {
    return input.inlineImageUrl;
  }

  if (input.imageSource === 'Url' || input.externalUrl)
  {
    return input.externalUrl;
  }

  return undefined;
}

export function alignmentClass(alignment?: FormImageAlignment): string {
  const value = normalizeImageAlignment(alignment);
  if (value === 'Left')
  {
    return 'justify-start text-left';
  }

  if (value === 'Right')
  {
    return 'justify-end text-right';
  }

  return 'justify-center text-center';
}

export function resolveMaskSettings(field: FormField): FormFieldMaskSettings {
  const settings = parseFormFieldSettings(field.settingsJson);
  const kind = FORM_FIELD_MASK_OPTIONS.some((item) => item.value === settings.mask?.kind)
    ? (settings.mask?.kind as FormFieldMaskKind)
    : 'None';

  return {
    kind,
    pattern: kind === 'Custom' ? settings.mask?.pattern : undefined,
  };
}

export function buildMaskSettingsJson(
  kind: FormFieldMaskKind,
  pattern: string | undefined,
  existingSettingsJson?: string
): string {
  const current = parseFormFieldSettings(existingSettingsJson);
  if (kind === 'None')
  {
    const { mask: _removed, ...rest } = current;
    return serializeFormFieldSettings(rest);
  }

  return serializeFormFieldSettings({
    ...current,
    mask: {
      kind,
      pattern: kind === 'Custom' ? pattern?.trim() || undefined : undefined,
    },
  });
}

export function resolveContactBinding(field: FormField): FormFieldContactBinding {
  const settings = parseFormFieldSettings(field.settingsJson);
  const value = settings.contactBinding;
  if (FORM_FIELD_CONTACT_BINDING_OPTIONS.some((item) => item.value === value))
  {
    return value as FormFieldContactBinding;
  }

  return 'None';
}

export function buildContactBindingSettingsJson(
  binding: FormFieldContactBinding,
  existingSettingsJson?: string
): string {
  const current = parseFormFieldSettings(existingSettingsJson);
  if (binding === 'None')
  {
    const { contactBinding: _removed, ...rest } = current;
    return serializeFormFieldSettings(rest);
  }

  return serializeFormFieldSettings({
    ...current,
    contactBinding: binding,
  });
}

export function formHasContactBindings(fields: FormField[]): boolean {
  return fields.some((field) => resolveContactBinding(field) !== 'None');
}

export function stripContactBindingsFromFields(fields: FormField[]): FormField[] {
  return fields.map((field) =>
  {
    if (resolveContactBinding(field) === 'None')
    {
      return field;
    }

    return {
      ...field,
      settingsJson: buildContactBindingSettingsJson('None', field.settingsJson),
    };
  });
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function applyCustomMask(value: string, pattern: string, strip: boolean): string {
  if (!pattern)
  {
    return strip ? digitsOnly(value) : value;
  }

  if (strip)
  {
    let valueIndex = 0;
    let result = '';
    for (const maskChar of pattern)
    {
      if (maskChar === '9' || maskChar === 'A' || maskChar === '*')
      {
        while (valueIndex < value.length)
        {
          const ch = value[valueIndex];
          valueIndex += 1;
          if (maskChar === '9' && /\d/.test(ch))
          {
            result += ch;
            break;
          }

          if (maskChar === 'A' && /[A-Za-z]/.test(ch))
          {
            result += ch;
            break;
          }

          if (maskChar === '*' && /[A-Za-z0-9]/.test(ch))
          {
            result += ch;
            break;
          }
        }
      }
    }

    return result;
  }

  let sourceIndex = 0;
  let formatted = '';
  const source = value;
  for (const maskChar of pattern)
  {
    if (maskChar === '9' || maskChar === 'A' || maskChar === '*')
    {
      if (sourceIndex >= source.length)
      {
        break;
      }

      formatted += source[sourceIndex];
      sourceIndex += 1;
    }
    else
    {
      formatted += maskChar;
    }
  }

  return formatted;
}

export function formatMaskedInput(
  rawValue: string,
  mask: FormFieldMaskSettings | undefined
): string {
  const kind = mask?.kind ?? 'None';
  if (kind === 'None')
  {
    return rawValue;
  }

  if (kind === 'Cpf')
  {
    const digits = digitsOnly(rawValue).slice(0, 11);
    if (digits.length <= 3)
    {
      return digits;
    }

    if (digits.length <= 6)
    {
      return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    }

    if (digits.length <= 9)
    {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }

    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  if (kind === 'Cnpj')
  {
    const chars = rawValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 14);
    if (chars.length <= 2)
    {
      return chars;
    }

    if (chars.length <= 5)
    {
      return `${chars.slice(0, 2)}.${chars.slice(2)}`;
    }

    if (chars.length <= 8)
    {
      return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
    }

    if (chars.length <= 12)
    {
      return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
    }

    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
  }

  if (kind === 'Phone')
  {
    const digits = digitsOnly(rawValue).slice(0, 11);
    if (digits.length <= 2)
    {
      return digits;
    }

    if (digits.length <= 6)
    {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10)
    {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  if (kind === 'Cep')
  {
    const digits = digitsOnly(rawValue).slice(0, 8);
    if (digits.length <= 5)
    {
      return digits;
    }

    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  if (kind === 'Currency')
  {
    const digits = digitsOnly(rawValue);
    if (!digits)
    {
      return '';
    }

    const amount = Number.parseInt(digits, 10) / 100;
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (kind === 'Custom')
  {
    const stripped = applyCustomMask(rawValue, mask?.pattern ?? '', true);
    return applyCustomMask(stripped, mask?.pattern ?? '', false);
  }

  return rawValue;
}

export function canonicalizeMaskedValue(
  value: string,
  mask: FormFieldMaskSettings | undefined
): string {
  const trimmed = value.trim();
  if (!trimmed)
  {
    return '';
  }

  const kind = mask?.kind ?? 'None';
  if (kind === 'None')
  {
    return trimmed;
  }

  if (kind === 'Cpf' || kind === 'Phone' || kind === 'Cep')
  {
    return digitsOnly(trimmed);
  }

  if (kind === 'Cnpj')
  {
    return trimmed.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  }

  if (kind === 'Currency')
  {
    const hasComma = trimmed.includes(',');
    let normalized = trimmed.replace(/R\$/gi, '').replace(/\s/g, '');
    if (hasComma)
    {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }
    else
    {
      normalized = normalized.replace(/,/g, '');
    }

    const amount = Number.parseFloat(normalized);
    if (Number.isFinite(amount))
    {
      return String(Number(amount.toFixed(2)));
    }

    return digitsOnly(trimmed);
  }

  if (kind === 'Custom')
  {
    return applyCustomMask(trimmed, mask?.pattern ?? '', true);
  }

  return trimmed;
}

function isValidCpf(digits: string): boolean {
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits))
  {
    return false;
  }

  const nums = digits.split('').map((digit) => Number(digit));
  let sum1 = 0;
  for (let i = 0; i < 9; i++)
  {
    sum1 += nums[i] * (10 - i);
  }

  let check1 = (sum1 * 10) % 11;
  if (check1 === 10)
  {
    check1 = 0;
  }

  if (check1 !== nums[9])
  {
    return false;
  }

  let sum2 = 0;
  for (let i = 0; i < 10; i++)
  {
    sum2 += nums[i] * (11 - i);
  }

  let check2 = (sum2 * 10) % 11;
  if (check2 === 10)
  {
    check2 = 0;
  }

  return check2 === nums[10];
}

function isValidCnpj(raw: string): boolean {
  if (raw.length !== 14 || !/^[0-9A-Z]{12}\d{2}$/.test(raw))
  {
    return false;
  }

  const values = raw.split('').map((char) => {
    const code = char.charCodeAt(0);
    if (code >= 48 && code <= 57)
    {
      return code - 48;
    }

    if (code >= 65 && code <= 90)
    {
      return code - 48;
    }

    return -1;
  });

  if (values.some((value) => value < 0))
  {
    return false;
  }

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const calc = (slice: number[], weights: number[]) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++)
    {
      sum += slice[i] * weights[i];
    }

    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  if (calc(values.slice(0, 12), firstWeights) !== values[12])
  {
    return false;
  }

  return calc(values.slice(0, 13), secondWeights) === values[13];
}

export function validateMaskedValue(
  value: string,
  mask: FormFieldMaskSettings | undefined,
  fieldTitle: string
): string | null {
  const canonical = canonicalizeMaskedValue(value, mask);
  if (!canonical)
  {
    return null;
  }

  const kind = mask?.kind ?? 'None';
  if (kind === 'Cpf' && !isValidCpf(canonical))
  {
    return `O campo "${fieldTitle}" exige um CPF válido.`;
  }

  if (kind === 'Cnpj' && !isValidCnpj(canonical))
  {
    return `O campo "${fieldTitle}" exige um CNPJ válido.`;
  }

  if (kind === 'Phone' && canonical.length !== 10 && canonical.length !== 11)
  {
    return `O campo "${fieldTitle}" exige um telefone válido.`;
  }

  if (kind === 'Cep' && canonical.length !== 8)
  {
    return `O campo "${fieldTitle}" exige um CEP válido.`;
  }

  if (kind === 'Currency' && Number.isNaN(Number.parseFloat(canonical)))
  {
    return `O campo "${fieldTitle}" exige um valor monetário válido.`;
  }

  return null;
}
